<template>
    <NuxtLayout name="malt-base-layout">
        <LoadingIndicator />
        <VJoyWrapper v-if="organizationDetails" no-margin column-gap="8" align="flex-start" wrap="nowrap" class="organization-settings">
            <OrganizationSettingsNavbar />
            <KeepAlive>
                <div class="organization-settings__wrapper">
                    <VJoyAdminBanner v-if="isPlatformAdmin" class="organization-settings__admin-banner" />
                    <div class="organization-settings__container">
                        <slot />
                    </div>
                </div>
            </KeepAlive>
        </VJoyWrapper>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {computed, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {LoadingIndicator} from '#components';
    import {useAuth, useRoute} from '#imports';
    import {VJoyWrapper, VJoyAdminBanner} from '@maltjoy/core-vue';
    import {useOrganizationDetailsStore} from '~/features/organization-settings/organization-details.store';
    import {useOrganizationMemberStore} from '~/features/team/members/organization-member.store';
    import {useLegalEntityStore} from '~/store/organization-settings/legal-entity-store';
    import OrganizationSettingsNavbar from '../components/organization-settings/Navbar.vue';

    const auth = useAuth();
    const {user} = storeToRefs(auth);
    const isPlatformAdmin = auth.hasEnoughPrivilege(['ROLE_ADMIN']);

    const route = useRoute();

    let legalEntityStore = useLegalEntityStore();
    const {legalEntityForm} = storeToRefs(legalEntityStore);

    const organizationId = computed(() => (route.params.id as string | undefined) || legalEntityForm.value.organizationId);
    await legalEntityStore.fetchLegalEntities(organizationId.value);
    const organizationMemberStore = useOrganizationMemberStore();
    const organizationDetailsStore = useOrganizationDetailsStore();
    const {organizationDetails} = storeToRefs(organizationDetailsStore);

    // Initialize organization details here because it's the only common component between organization and legal entity pages
    watch(
        () => organizationId.value,
        async () => {
            if (organizationId.value !== '') {
                await organizationDetailsStore.init(organizationId.value);
                await organizationMemberStore.initOrganizationRole(organizationId.value, user.value!.id);
            }
        },
        {immediate: true},
    );

    // Refetch the legal entities whenever the organization changes (e.g. platform admin switching org from the filter).
    // The layout is not re-created on a param-only navigation, so the top-level fetch above never re-runs on its own.
    watch(
        () => organizationId.value,
        async (newOrganizationId) => {
            if (newOrganizationId !== '') {
                await legalEntityStore.fetchLegalEntities(newOrganizationId);
            }
        },
    );
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint' as joy;

    body {
        background-color: var(--joy-color-neutral-10);
        padding-left: 290px;

        @include joy.screen_sm {
            padding-left: 0;
        }
    }

    .organization-settings {
        &__wrapper {
            max-width: calc(100% - 16rem);
            width: 100%;

            @include joy.screen_sm {
                max-width: 100%;
            }
        }

        &__admin-banner {
            margin-top: -2px; // To compensate LoadingIndicator height
            z-index: auto !important;
        }

        &__container {
            margin: auto;
            max-width: 1440px;

            @include joy.screen_sm {
                padding-top: var(--joy-core-spacing-8);
            }
        }

        .joy-link {
            text-decoration: none;
            font-weight: var(--joy-font-weight-normal);

            &[disabled='true'],
            &[disabled='true']:hover {
                cursor: not-allowed;
                span {
                    color: var(--joy-color-neutral-40);
                    text-decoration: none;
                }
            }
        }
    }
</style>
